import { useState, useEffect, useMemo, useRef, useCallback } from "react"
import { listNodes, saveNode, moveNode, addParent, removeParent, deleteNode } from "../api"

const SYNTHETIC_ROOT = { id: 1, name: "Taxonomy", slug: "_root", color: "#6366f1" }

export function useTaxonomyWithDatabase() {
  const [loading, setLoading] = useState(true)
  const [operationInProgress, setOperationInProgress] = useState(false)
  const [nodesData, setNodesData] = useState(null)
  const [reloadTrigger, setReloadTrigger] = useState(0)
  const isFirstLoad = useRef(true)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      if (isFirstLoad.current) setLoading(true)
      try {
        const res = await listNodes({ includeAll: true })
        if (!cancelled) setNodesData(res?.list || [])
      } catch (err) {
        console.error("[Taxonomy] Failed to load nodes:", err)
        if (!cancelled) setNodesData([])
      } finally {
        if (!cancelled && isFirstLoad.current) {
          setLoading(false)
          isFirstLoad.current = false
        }
      }
    }
    load()
    return () => { cancelled = true }
  }, [reloadTrigger])

  const reload = useCallback(() => setReloadTrigger((n) => n + 1), [])

  const withTracking = useCallback(
    (op) =>
      async (...args) => {
        setOperationInProgress(true)
        try {
          return await op(...args)
        } finally {
          setOperationInProgress(false)
        }
      },
    []
  )

  // Numeric ids must stay STABLE across reloads — the breadcrumb and
  // selection hold onto them. Assigning ids by fetch order reshuffles them
  // whenever a create/rename changes the sort order, orphaning the current
  // breadcrumb ("No children" until a hard refresh). Persist the _id→numId
  // assignment for the lifetime of the manager.
  const persistentIdsRef = useRef({ byMongoId: new Map(), next: 2 })

  // Build idMap and reverseIdMap from raw CMS nodes
  const { initialData, idMap, reverseIdMap } = useMemo(() => {
    if (!nodesData)
      return { initialData: null, idMap: new Map(), reverseIdMap: new Map() }

    const persistent = persistentIdsRef.current
    const idMap = new Map()     // _id string → numeric id
    const reverseIdMap = new Map() // numeric id → { _id, slug }

    for (const node of nodesData) {
      const idStr = String(node._id)
      let numId = persistent.byMongoId.get(idStr)
      if (numId === undefined) {
        numId = persistent.next++
        persistent.byMongoId.set(idStr, numId)
      }
      idMap.set(idStr, numId)
      reverseIdMap.set(numId, { _id: node._id, slug: node.content.slug })
    }

    const categories = [SYNTHETIC_ROOT]
    const relations = []

    for (const node of nodesData) {
      const numId = idMap.get(String(node._id))
      categories.push({
        id: numId,
        name: node.content.name || node.content.slug,
        slug: node.content.slug,
        color: node.content.color || "#607D8B",
      })

      const parents = node.content.parents || []
      if (parents.length === 0) {
        relations.push({ parentId: 1, childId: numId })
      } else {
        for (const ref of parents) {
          const parentNumId = idMap.get(String(ref._id))
          relations.push({ parentId: parentNumId ?? 1, childId: numId })
        }
      }
    }

    return {
      initialData: { categories, relations, rootCategory: SYNTHETIC_ROOT },
      idMap,
      reverseIdMap,
    }
  }, [nodesData])

  const operations = useMemo(() => {
    if (!initialData) return {}

    return {
      onCreate: withTracking(async (category, parentId) => {
        await saveNode({
          slug: category.slug,
          data: { name: category.name, color: category.color, status: "draft" },
        })
        const parent = reverseIdMap.get(parentId)
        if (parent?.slug && parent.slug !== "_root") {
          await addParent([category.slug], parent.slug)
        }
        reload()
      }),

      onBulkCreate: withTracking(async (categories, parentId) => {
        for (const cat of categories) {
          await saveNode({
            slug: cat.slug,
            data: { name: cat.name, color: cat.color, status: "draft" },
          })
          const parent = reverseIdMap.get(parentId)
          if (parent?.slug && parent.slug !== "_root") {
            await addParent([cat.slug], parent.slug)
          }
        }
        reload()
      }),

      onRename: withTracking(async (categoryId, newName, newColor) => {
        const item = reverseIdMap.get(categoryId)
        if (!item) return
        await saveNode({
          _id: String(item._id),
          slug: item.slug,
          data: { name: newName, color: newColor },
        })
        reload()
      }),

      onMove: withTracking(async (categoryIds, newParentId) => {
        const slugs = categoryIds.map((id) => reverseIdMap.get(id)?.slug).filter(Boolean)
        const parentSlug = reverseIdMap.get(newParentId)?.slug ?? "_root"
        if (!slugs.length) return
        await moveNode(slugs, parentSlug)
        reload()
      }),

      onDelete: withTracking(async (categoryIds) => {
        for (const id of categoryIds) {
          const item = reverseIdMap.get(id)
          if (item) await deleteNode(item.slug)
        }
        reload()
      }),

      onAddRelation: withTracking(async (parentId, childId) => {
        const child = reverseIdMap.get(childId)
        const parent = reverseIdMap.get(parentId)
        if (!child || !parent) return
        await addParent([child.slug], parent.slug)
        reload()
      }),

      onRemoveRelation: withTracking(async (parentId, childId) => {
        const child = reverseIdMap.get(childId)
        const parent = reverseIdMap.get(parentId)
        if (!child || !parent) return
        await removeParent([child.slug], parent.slug)
        reload()
      }),
    }
  }, [initialData, reverseIdMap, withTracking, reload])

  return { initialData, operations, loading, operationInProgress, reload, reverseIdMap }
}
